import { useState, useEffect, useRef } from 'react'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { Alert } from '../../../components/ui/Alert'
import { selectClass, inputClass } from '../../../components/ui/FormField'
import { api } from '../../../api/axios'
import { useAuth } from '../../../contexts/AuthContext'
import { useAtiradores } from '../../atiradores/hooks/useAtiradores'
import { EnvioLoteAnexoE } from '../components/EnvioLoteAnexoE'
import { DataBR } from '../../../components/ui/DataBR'
import { dataLocalISO } from '../../../lib/dataLocal'

const TIPOS_ARMA = ['Pistola', 'Revolver', 'Carabina', 'Espingarda', 'Rifle', 'Outro']

interface PdfResult { arquivo: string; url: string; whatsapp: string }

// ── Modal de visualizacao do PDF ─────────────────────────────────────────────
function PdfPreviewModal({
  result,
  titulo,
  idatirador,
  onClose,
}: {
  result: PdfResult
  titulo: string
  idatirador?: number
  onClose: () => void
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [envio, setEnvio] = useState<{ ok: boolean; msg: string } | null>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    let active = true
    let createdUrl: string | null = null
    setLoading(true)
    setErro(null)
    api
      .get(`/relatorios/arquivo/${result.arquivo}`, { responseType: 'blob' })
      .then(resp => {
        if (!active) return
        const blob = new Blob([resp.data], { type: 'application/pdf' })
        createdUrl = URL.createObjectURL(blob)
        setBlobUrl(createdUrl)
      })
      .catch(err => {
        if (!active) return
        setErro(err?.response?.data?.error || err?.message || 'Erro ao carregar PDF')
      })
      .finally(() => { if (active) setLoading(false) })
    return () => {
      active = false
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [result.arquivo])

  function handlePrint() {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus()
      iframeRef.current.contentWindow.print()
    }
  }

  function handleDownload() {
    if (!blobUrl) return
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = result.arquivo
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  // Envia o PDF ja gerado para o e-mail cadastrado do atirador
  async function handleEnviarEmail() {
    setEnviando(true); setEnvio(null)
    try {
      const { data } = await api.post<{ data: { email: string } }>('/relatorios/declaracao/email', {
        arquivo: result.arquivo,
        ...(idatirador ? { idatirador } : {}),
      })
      setEnvio({ ok: true, msg: `Enviado para ${data.data.email}` })
    } catch (err: any) {
      setEnvio({ ok: false, msg: err?.response?.data?.message || err?.response?.data?.error || 'Erro ao enviar o e-mail' })
    } finally { setEnviando(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex flex-col" onClick={onClose}>
      <div className="flex-1 flex flex-col bg-compumaq-pretoSuave m-4 rounded-xl overflow-hidden shadow-2xl"
           onClick={e => e.stopPropagation()}>
        <div className="bg-compumaq-pretoSuave border-b px-4 py-3 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-semibold text-compumaq-texto">{titulo}</h3>
            <p className="text-xs text-compumaq-textoSuave">Pre-visualizacao</p>
            {envio && (
              <p className={`text-xs mt-1 ${envio.ok ? 'text-green-500' : 'text-red-400'}`}>{envio.msg}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={handlePrint} disabled={!blobUrl}
              className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50">
              Imprimir
            </button>
            <button type="button" onClick={handleDownload} disabled={!blobUrl}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
              Baixar PDF
            </button>
            <button type="button" onClick={handleEnviarEmail} disabled={enviando || envio?.ok}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {enviando ? 'Enviando...' : envio?.ok ? 'E-mail enviado' : 'Enviar por e-mail'}
            </button>
            <button type="button" onClick={onClose}
              className="px-4 py-2 bg-compumaq-pretoSuave text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-pretoSuave">
              Fechar
            </button>
          </div>
        </div>
        <div className="flex-1 bg-compumaq-pretoSuave relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-compumaq-pretoSuave rounded-xl px-6 py-4 flex items-center gap-3 shadow">
                <Spinner size="lg" /> <span className="text-compumaq-texto">Carregando PDF...</span>
              </div>
            </div>
          )}
          {erro && (
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md">
                <p className="font-semibold text-red-800 mb-2">Erro ao carregar PDF</p>
                <p className="text-sm text-red-700">{erro}</p>
              </div>
            </div>
          )}
          {blobUrl && !erro && (
            <iframe ref={iframeRef} src={blobUrl} className="w-full h-full border-0" title={titulo} />
          )}
        </div>
      </div>
    </div>
  )
}

// ── Tipos para vinculacao de armas ────────────────────────────────────────────
interface InscricaoVinculo {
  idinscricao: number
  campeonato_descricao: string
  modalidade_nome: string
  etapa_titulo: string
  data_inicio: string | null
  idarma: number | null
  arma_descricao: string | null
}

interface ArmaOpcao {
  idarma: number
  label: string
}

interface VinculoLocal {
  idinscricao: number
  idarma: number | null
}

// ── helpers de data ───────────────────────────────────────────────────────────
function toDateInputValue(d: Date): string {
  return dataLocalISO(d)
}

function defaultDataInicio(): string {
  const d = new Date()
  d.setFullYear(d.getFullYear() - 1)
  return toDateInputValue(d)
}

function defaultDataFim(): string {
  return toDateInputValue(new Date())
}

// ── Modal de vinculacao de armas ──────────────────────────────────────────────
function VinculacaoArmasModal({
  idatirador,
  ano,
  dataInicio,
  dataFim,
  onClose,
  onSalvo,
}: {
  idatirador: number
  ano: number
  dataInicio: string
  dataFim: string
  onClose: () => void
  onSalvo: () => void
}) {
  const [inscricoes, setInscricoes] = useState<InscricaoVinculo[]>([])
  const [armas, setArmas] = useState<ArmaOpcao[]>([])
  const [vinculos, setVinculos] = useState<VinculoLocal[]>([])
  const [loading, setLoading] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [salvo, setSalvo] = useState(false)

  // Form de nova arma
  const [showNovaArma, setShowNovaArma] = useState(false)
  const [novaArmaForm, setNovaArmaForm] = useState({ marca: '', modelo: '', calibre: '', tipo: 'Pistola', numero_serie: '', numero_sigma: '' })
  const [salvandoArma, setSalvandoArma] = useState(false)
  const [erroArma, setErroArma] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    setLoading(true)
    setErro(null)
    api
      .get('/inscricoes/atirador-ano', { params: { idatirador, ano, data_inicio: dataInicio, data_fim: dataFim } })
      .then(res => {
        if (!active) return
        const { inscricoes: insc, armas: ar } = res.data.data
        setInscricoes(insc)
        setArmas(ar)
        setVinculos(insc.map((i: InscricaoVinculo) => ({ idinscricao: Number(i.idinscricao), idarma: i.idarma !== null ? Number(i.idarma) : null })))
      })
      .catch(err => {
        if (!active) return
        setErro(err?.response?.data?.error || 'Erro ao carregar inscricoes')
      })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [idatirador, ano, dataInicio, dataFim])

  function setArmaVinculo(idinscricao: number, idarma: number | null) {
    setSalvo(false)
    setVinculos(prev => prev.map(v => v.idinscricao === idinscricao ? { ...v, idarma } : v))
  }

  async function handleSalvar() {
    setSalvando(true)
    setErro(null)
    try {
      await api.put('/inscricoes/vincular-armas', { vinculos })
      setSalvo(true)
    } catch (err: any) {
      setErro(err?.response?.data?.error || 'Erro ao salvar vinculos')
    } finally {
      setSalvando(false)
    }
  }

  async function handleCadastrarArma(e: React.FormEvent) {
    e.preventDefault()
    setErroArma(null)
    if (!novaArmaForm.marca || !novaArmaForm.modelo || !novaArmaForm.calibre) {
      setErroArma('Marca, Modelo e Calibre sao obrigatorios')
      return
    }
    setSalvandoArma(true)
    try {
      const payload: Record<string, unknown> = { ...novaArmaForm }
      if (idatirador) payload.idatirador = idatirador
      const res = await api.post('/publico/minhas-armas', payload)
      const novaArma = res.data.data
      setArmas(prev => [...prev, { idarma: novaArma.idarma, label: `${novaArma.tipo} ${novaArma.marca} ${novaArma.modelo} ${novaArma.calibre}${novaArma.numero_sigma ? ' — SIGMA: ' + novaArma.numero_sigma : ''}` }])
      setNovaArmaForm({ marca: '', modelo: '', calibre: '', tipo: 'Pistola', numero_serie: '', numero_sigma: '' })
      setShowNovaArma(false)
      setSalvo(false)
    } catch (err: any) {
      setErroArma(err?.response?.data?.error || 'Erro ao cadastrar arma')
    } finally {
      setSalvandoArma(false)
    }
  }

  function formatData(dt: string | null) {
    if (!dt) return '-'
    try {
      return new Date(dt).toLocaleDateString('pt-BR')
    } catch {
      return dt
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-compumaq-pretoSuave rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-compumaq-texto">Vincular Armas — Anexo E</h2>
            <p className="text-sm text-compumaq-textoSuave mt-0.5">
              Periodo: {new Date(dataInicio + 'T12:00:00').toLocaleDateString('pt-BR')} a {new Date(dataFim + 'T12:00:00').toLocaleDateString('pt-BR')} — Selecione a arma utilizada em cada competicao.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-compumaq-textoSuave hover:text-compumaq-textoSuave text-xl leading-none mt-0.5"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-12 gap-3">
              <Spinner size="lg" />
              <span className="text-compumaq-textoSuave">Carregando inscricoes...</span>
            </div>
          )}

          {erro && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{erro}</div>
          )}

          {/* Botao + Adicionar Arma */}
          {!loading && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-compumaq-textoSuave">Armas disponíveis: {armas.length}</p>
              <button
                type="button"
                onClick={() => { setShowNovaArma(v => !v); setErroArma(null) }}
                className="flex items-center gap-1.5 text-xs font-medium text-compumaq-doradoClaro bg-compumaq-dourado/10 hover:bg-compumaq-dourado/15 px-3 py-1.5 rounded-lg transition-colors"
              >
                {showNovaArma ? '— Cancelar Nova Arma' : '+ Adicionar Arma'}
              </button>
            </div>
          )}

          {/* Mini-formulario Nova Arma */}
          {showNovaArma && !loading && (
            <form
              onSubmit={handleCadastrarArma}
              className="bg-compumaq-dourado/10 border border-compumaq-dourado/25 rounded-lg p-4 space-y-3"
            >
              <h3 className="text-sm font-semibold text-compumaq-doradoClaro">Cadastrar Nova Arma</h3>
              {erroArma && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{erroArma}</p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-compumaq-texto mb-1">Tipo *</label>
                  <select
                    className={selectClass + ' text-sm'}
                    value={novaArmaForm.tipo}
                    onChange={e => setNovaArmaForm(f => ({ ...f, tipo: e.target.value }))}
                  >
                    {TIPOS_ARMA.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-compumaq-texto mb-1">Marca *</label>
                  <input
                    className={inputClass + ' text-sm'}
                    value={novaArmaForm.marca}
                    onChange={e => setNovaArmaForm(f => ({ ...f, marca: e.target.value }))}
                    placeholder="Ex: Taurus"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-compumaq-texto mb-1">Modelo *</label>
                  <input
                    className={inputClass + ' text-sm'}
                    value={novaArmaForm.modelo}
                    onChange={e => setNovaArmaForm(f => ({ ...f, modelo: e.target.value }))}
                    placeholder="Ex: PT92"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-compumaq-texto mb-1">Calibre *</label>
                  <input
                    className={inputClass + ' text-sm'}
                    value={novaArmaForm.calibre}
                    onChange={e => setNovaArmaForm(f => ({ ...f, calibre: e.target.value }))}
                    placeholder="Ex: 9MM"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-compumaq-texto mb-1">N. Serie</label>
                  <input
                    className={inputClass + ' text-sm'}
                    value={novaArmaForm.numero_serie}
                    onChange={e => setNovaArmaForm(f => ({ ...f, numero_serie: e.target.value }))}
                    placeholder="Opcional"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-compumaq-texto mb-1">SIGMA</label>
                  <input
                    className={inputClass + ' text-sm'}
                    value={novaArmaForm.numero_sigma}
                    onChange={e => setNovaArmaForm(f => ({ ...f, numero_sigma: e.target.value }))}
                    placeholder="Opcional"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => { setShowNovaArma(false); setErroArma(null) }}
                  className="px-3 py-1.5 text-xs border border-compumaq-dourado/25 rounded-lg text-compumaq-texto hover:bg-compumaq-preto"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={salvandoArma}
                  className="px-4 py-1.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-xs font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50"
                >
                  {salvandoArma ? 'Salvando...' : 'Cadastrar Arma'}
                </button>
              </div>
            </form>
          )}

          {!loading && !erro && inscricoes.length === 0 && (
            <div className="text-center py-12 text-compumaq-textoSuave">
              <p className="font-medium">Nenhuma inscricao encontrada para {ano}.</p>
              <p className="text-sm mt-1">Verifique se o atirador participou de competicoes neste ano.</p>
            </div>
          )}

          {!loading && !erro && inscricoes.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-compumaq-preto text-left">
                    <th className="px-3 py-2 text-xs font-semibold text-compumaq-textoSuave border-b">Campeonato / Etapa</th>
                    <th className="px-3 py-2 text-xs font-semibold text-compumaq-textoSuave border-b">Modalidade</th>
                    <th className="px-3 py-2 text-xs font-semibold text-compumaq-textoSuave border-b whitespace-nowrap">Data</th>
                    <th className="px-3 py-2 text-xs font-semibold text-compumaq-textoSuave border-b min-w-[260px]">Arma utilizada</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-compumaq-dourado/25">
                  {inscricoes.map(insc => {
                    const vinculo = vinculos.find(v => v.idinscricao === insc.idinscricao)
                    const armaSelecionada = vinculo?.idarma ?? null
                    return (
                      <tr key={insc.idinscricao} className="hover:bg-compumaq-preto">
                        <td className="px-3 py-2">
                          <p className="font-medium text-compumaq-texto">{insc.campeonato_descricao}</p>
                          <p className="text-xs text-compumaq-textoSuave">{insc.etapa_titulo}</p>
                        </td>
                        <td className="px-3 py-2 text-compumaq-texto">{insc.modalidade_nome}</td>
                        <td className="px-3 py-2 text-compumaq-textoSuave whitespace-nowrap">{formatData(insc.data_inicio)}</td>
                        <td className="px-3 py-2">
                          {armas.length === 0 ? (
                            <span className="text-xs text-compumaq-textoSuave italic">Sem armas cadastradas</span>
                          ) : (
                            <select
                              className={selectClass + ' text-sm'}
                              value={armaSelecionada ?? ''}
                              onChange={e => setArmaVinculo(
                                insc.idinscricao,
                                e.target.value ? parseInt(e.target.value) : null,
                              )}
                            >
                              <option value="">-- Selecionar arma --</option>
                              {armas.map(ar => (
                                <option key={ar.idarma} value={ar.idarma}>{ar.label}</option>
                              ))}
                            </select>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !erro && inscricoes.length > 0 && (
          <div className="px-6 py-4 border-t flex flex-wrap items-center justify-between gap-3">
            <div>
              {salvo && (
                <p className="text-sm text-green-600 font-medium">Armas vinculadas com sucesso!</p>
              )}
              {erro && (
                <p className="text-sm text-red-600">{erro}</p>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-compumaq-pretoSuave text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-pretoSuave"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSalvar}
                disabled={salvando}
                className="px-5 py-2 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50"
              >
                {salvando ? 'Salvando...' : 'Salvar Armas'}
              </button>
              <button
                type="button"
                onClick={onSalvo}
                disabled={!salvo}
                className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title={!salvo ? 'Salve as armas primeiro' : 'Gerar PDF'}
              >
                Gerar PDF
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Pagina de Declaracoes ──────────────────────────────────────────────────
type Tab = 'filiacao' | 'calendario' | 'frequencia' | 'lote'

export function DeclaracoesPage() {
  const { hasRole } = useAuth()
  const isAdmin = hasRole('admin')

  const [tab, setTab] = useState<Tab>('filiacao')
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  // Para admin: pode escolher o atirador
  const [selAtirador, setSelAtirador] = useState<number | ''>('')
  const [searchAtirador, setSearchAtirador] = useState('')
  const { data: atiradoresData } = useAtiradores(
    isAdmin && searchAtirador.length >= 3 ? { nome: searchAtirador, limit: 20 } : {},
  )
  const atiradores = atiradoresData?.data ?? []

  const [ano, setAno] = useState<number>(new Date().getFullYear())
  const [dataInicio, setDataInicio] = useState<string>(defaultDataInicio())
  const [dataFim, setDataFim] = useState<string>(defaultDataFim())
  const [pdf, setPdf] = useState<PdfResult | null>(null)
  const [titulo, setTitulo] = useState('')
  const [gerando, setGerando] = useState(false)

  // Controle do modal de vinculacao
  const [showVinculacao, setShowVinculacao] = useState(false)

  async function gerarFiliacao() {
    setAlert(null); setPdf(null); setGerando(true)
    try {
      const params: any = {}
      if (isAdmin && selAtirador) params.idatirador = selAtirador
      const { data } = await api.get<{ data: PdfResult }>('/relatorios/declaracao-filiacao', { params })
      setTitulo('Declaracao de Filiacao')
      setPdf(data.data)
    } catch (err: any) {
      setAlert({ type: 'error', message: err?.response?.data?.error || 'Erro ao gerar declaracao' })
    } finally { setGerando(false) }
  }

  async function gerarCalendario() {
    setAlert(null); setPdf(null); setGerando(true)
    try {
      const params: any = { ano, data_inicio: dataInicio, data_fim: dataFim }
      if (isAdmin && selAtirador) params.idatirador = selAtirador
      const { data } = await api.get<{ data: PdfResult }>('/relatorios/comprovacao-calendario', { params })
      setTitulo('Comprovacao de Calendario Anual')
      setPdf(data.data)
    } catch (err: any) {
      setAlert({ type: 'error', message: err?.response?.data?.error || 'Erro ao gerar comprovacao' })
    } finally { setGerando(false) }
  }

  async function gerarFrequencia() {
    setAlert(null); setPdf(null); setGerando(true)
    try {
      const params: any = { data_inicio: dataInicio, data_fim: dataFim }
      if (isAdmin && selAtirador) params.idatirador = selAtirador
      const { data } = await api.get<{ data: PdfResult }>('/relatorios/declaracao-frequencia', { params })
      setTitulo('Declaracao de Frequencia')
      setPdf(data.data)
    } catch (err: any) {
      setAlert({ type: 'error', message: err?.response?.data?.error || 'Erro ao gerar declaracao de frequencia' })
    } finally { setGerando(false) }
  }

  // Chamado pelo modal apos salvar armas - fecha modal e gera PDF
  async function handleVinculacaoSalvaEGerar() {
    setShowVinculacao(false)
    await gerarCalendario()
  }

  const podeGerar = !isAdmin || !!selAtirador

  // idatirador efetivo para o modal (para atirador nao-admin, sera resolvido pelo backend)
  const idatiradorModal = isAdmin ? (selAtirador as number) : 0

  return (
    <div className="space-y-4">
      <PageHeader title="Declaracoes" description="Geracao de declaracoes oficiais (Anexo B e Anexo E - Decreto 11.615/2023)" />

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      {/* Tabs */}
      <div className="flex gap-1 bg-compumaq-pretoSuave rounded-lg p-1 flex-wrap">
        <button
          onClick={() => { setTab('filiacao'); setPdf(null) }}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${tab === 'filiacao' ? 'bg-compumaq-pretoSuave shadow text-compumaq-texto' : 'text-compumaq-textoSuave hover:text-compumaq-texto'}`}
        >Declaracao de Filiacao (Anexo B)</button>
        <button
          onClick={() => { setTab('calendario'); setPdf(null) }}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${tab === 'calendario' ? 'bg-compumaq-pretoSuave shadow text-compumaq-texto' : 'text-compumaq-textoSuave hover:text-compumaq-texto'}`}
        >Comprovacao Calendario (Anexo E)</button>
        <button
          onClick={() => { setTab('frequencia'); setPdf(null) }}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${tab === 'frequencia' ? 'bg-compumaq-pretoSuave shadow text-compumaq-texto' : 'text-compumaq-textoSuave hover:text-compumaq-texto'}`}
        >Declaracao de Frequencia</button>
        {isAdmin && (
          <button
            onClick={() => { setTab('lote'); setPdf(null) }}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${tab === 'lote' ? 'bg-compumaq-pretoSuave shadow text-compumaq-texto' : 'text-compumaq-textoSuave hover:text-compumaq-texto'}`}
          >Envio em lote</button>
        )}
      </div>

      {tab === 'lote' && isAdmin && <EnvioLoteAnexoE onAlert={setAlert} />}

      {/* Selecao de atirador para admin */}
      {isAdmin && tab !== 'lote' && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm space-y-3">
          <h3 className="font-semibold text-compumaq-texto">Selecionar Atirador</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Buscar por nome (3+ letras)</label>
              <input
                type="text"
                className={inputClass}
                placeholder="Digite o nome do atirador..."
                value={searchAtirador}
                onChange={e => setSearchAtirador(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Atirador</label>
              <select
                className={selectClass}
                value={selAtirador}
                onChange={e => setSelAtirador(e.target.value ? parseInt(e.target.value) : '')}
              >
                <option value="">Selecione...</option>
                {atiradores.map(a => (
                  <option key={a.idatirador} value={a.idatirador}>{a.nome} — {a.cpf}</option>
                ))}
              </select>
            </div>
          </div>
          {searchAtirador.length >= 3 && atiradores.length === 0 && (
            <p className="text-xs text-compumaq-textoSuave">Nenhum atirador encontrado.</p>
          )}
        </div>
      )}

      {/* Conteudo da aba */}
      {tab === 'filiacao' && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-6 shadow-sm space-y-3">
          <h2 className="font-semibold text-compumaq-texto">Declaracao de Filiacao a Entidade de Tiro</h2>
          <p className="text-sm text-compumaq-textoSuave">
            Emite o Anexo B do Decreto no 11.615/2023, comprovando a filiacao do atirador a entidade de tiro.
          </p>
          <button
            onClick={gerarFiliacao}
            disabled={!podeGerar || gerando}
            className="px-5 py-2.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50"
          >
            {gerando ? 'Gerando...' : 'Gerar Declaracao'}
          </button>
        </div>
      )}

      {tab === 'calendario' && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-6 shadow-sm space-y-4">
          <h2 className="font-semibold text-compumaq-texto">Comprovacao de Calendario Anual</h2>
          <p className="text-sm text-compumaq-textoSuave">
            Emite o Anexo E do Decreto no 11.615/2023, listando as competicoes/treinamentos realizados no periodo e armas utilizadas.
            Antes de gerar, vincule as armas usadas em cada competicao.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Data de inicio</label>
              <DataBR
                className={inputClass}
                value={dataInicio}
                max={dataFim}
                onChange={e => setDataInicio(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Data de fim</label>
              <DataBR
                className={inputClass}
                value={dataFim}
                min={dataInicio}
                max={toDateInputValue(new Date())}
                onChange={e => setDataFim(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={() => { setDataInicio(defaultDataInicio()); setDataFim(defaultDataFim()) }}
                className="px-3 py-2 text-xs border border-compumaq-dourado/25 rounded-lg text-compumaq-textoSuave hover:bg-compumaq-preto w-full"
              >
                Ultimos 12 meses
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => { setAlert(null); setShowVinculacao(true) }}
              disabled={!podeGerar || gerando || !dataInicio || !dataFim}
              className="px-5 py-2.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50"
            >
              {gerando ? 'Gerando...' : 'Gerar Comprovacao'}
            </button>
          </div>
          <p className="text-xs text-compumaq-textoSuave">
            Ao clicar em Gerar Comprovacao, sera exibida uma tela para vincular as armas utilizadas em cada competicao do periodo.
          </p>
        </div>
      )}

      {tab === 'frequencia' && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-6 shadow-sm space-y-4">
          <h2 className="font-semibold text-compumaq-texto">Declaracao de Frequencia</h2>
          <p className="text-sm text-compumaq-textoSuave">
            Comprovacao de habitualidade emitida pela entidade (Decreto no 11.615/2023, art. 35 e IN DG/PF no 311/2025):
            lista as competicoes em que o atirador participou no periodo (com resultado lancado), conta os eventos distintos
            e indica se atinge o Nivel I (8 em 12 meses). Assinada pelo representante legal do clube.
            Por enquanto so competicoes - treinos ainda nao sao registrados no sistema.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Data de inicio</label>
              <DataBR className={inputClass} aria-label="Inicio frequencia" value={dataInicio} max={dataFim} onChange={e => setDataInicio(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Data de fim</label>
              <DataBR className={inputClass} aria-label="Fim frequencia" value={dataFim} min={dataInicio} onChange={e => setDataFim(e.target.value)} />
            </div>
            <div className="flex items-end">
              <button type="button" onClick={() => { setDataInicio(defaultDataInicio()); setDataFim(defaultDataFim()) }}
                className="px-3 py-2 text-xs border border-compumaq-dourado/25 rounded-lg text-compumaq-textoSuave hover:bg-compumaq-preto w-full">
                Ultimos 12 meses
              </button>
            </div>
          </div>
          <button
            onClick={gerarFrequencia}
            disabled={!podeGerar || gerando || !dataInicio || !dataFim}
            className="px-5 py-2.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50"
          >
            {gerando ? 'Gerando...' : 'Gerar Declaracao de Frequencia'}
          </button>
        </div>
      )}

      {pdf && (
        <PdfPreviewModal
          result={pdf}
          titulo={titulo}
          idatirador={isAdmin && selAtirador ? selAtirador : undefined}
          onClose={() => setPdf(null)}
        />
      )}

      {showVinculacao && (
        <VinculacaoArmasModal
          idatirador={idatiradorModal}
          ano={ano}
          dataInicio={dataInicio}
          dataFim={dataFim}
          onClose={() => setShowVinculacao(false)}
          onSalvo={handleVinculacaoSalvaEGerar}
        />
      )}
    </div>
  )
}
